import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { environment, API } from '@core/envs';
import { ResponseModel, RevisionModel, IRevisionHistory, MaintenanceScheduleModel } from '@core/interface';
import { map, Observable, of, tap } from 'rxjs';
import { DealerService } from '../dealer/dealer.service';

@Injectable({
  providedIn: 'root'
})
export class RevisionService {

  readonly #http = inject(HttpClient);
  readonly #dealerService = inject(DealerService);

  readonly revisionInfoSubject = signal<RevisionModel | null>(null);

  // Signals para armazenar separadamente
  #revisionHistorySignal = signal<IRevisionHistory[]>([]);

  setRevisionHistory(revisionHistory: IRevisionHistory[] | null): void {
    this.#revisionHistorySignal.set(revisionHistory ?? []);
    sessionStorage.setItem('revisionHistory', JSON.stringify(revisionHistory ?? []));
  }

  getRevisionHistory(): IRevisionHistory[] {
    const revisionHistory = this.#revisionHistorySignal();

    // Se o signal possui valor, retorna imediatamente
    if (revisionHistory && revisionHistory.length > 0) {
      return revisionHistory;
    }

    // Se o signal está vazio, tenta recuperar do sessionStorage
    const revisionFromStorage = sessionStorage.getItem('revisionHistory');

    // Se houver dados no sessionStorage, tenta transformar ele em objeto, salvar no signal e retornar
    if (revisionFromStorage) {
      try {
        const revisionObject = JSON.parse(revisionFromStorage);
        this.setRevisionHistory(revisionObject);
        return revisionObject;

        // Se a conversão falhar, retorna um array vazio
      } catch {
        return [];
      }
    }
    // Se não houver dados no sessionStorage, retorna um array vazio
    return [];
  }

  // Método responsável pela busca na API
  getRevisionInfo(
    months: number,
    iolm: string,
    country: string,
    vin: string,
  ): Observable<RevisionModel | null> {
    // Verifica se já existe uma revisão armazenada para o mês solicitado
    // Se sim, retorna o valor armazenado
    if (this.revisionInfoSubject()?.monthService === months) {
      return of(this.revisionInfoSubject());
    }

    const id = this.#dealerService.getDealerId();
    const api = environment.getRoute(API.transformation);
    const url = `${api}/revisions/v2`;
    const body: any = { vin, country, revisionMonths: months };
    if (iolm === 'Y') {
      body.iolm = 'Y';
    }

    return this.#http.post<ResponseModel<RevisionModel[]>>(url, body, { params: { dealer: id } }).pipe(
      tap(data => this.revisionInfoSubject.set(data.response[0] ?? null)),
      // Always emit the revision model (or null)
      map(data => data.response[0] ?? null)
    );
  }


  // Busca os demais itens da revisão para exibição no accordion
  getMoreItems(
    vehicleLineFeatureCode: string,
    modelYear: string,
    country: string
  ): Observable<MaintenanceScheduleModel> {
    const api = environment.getRoute(API.owners);
    const url = `${api}/vehicles/maintenances?vehicleLine=${vehicleLineFeatureCode?.toLowerCase()}&modelYear=${modelYear}&country=${country}`;
    return this.#http.get<ResponseModel<MaintenanceScheduleModel>>(url).pipe(
      map(data => data.response)
    );
  }

}
